import { useEffect, useRef, useState } from 'react'
import TgEmojiPicker from './TgEmojiPicker'
import StickerPicker from './StickerPicker'
import GifPicker from './GifPicker'
import { Smile, Sticker, Film, X } from 'lucide-react'
import type { StickerDTO, GiphyItemDTO } from '../../services/api'

interface Props {
  /** Called with the emoji's native character (e.g. "😂") on selection. */
  onSelect: (emoji: string) => void
  /** Called when the drawer should close (X, Esc, outside click). */
  onClose: () => void
  /** Whether the drawer is currently open. */
  open?: boolean
  /** Called when the user picks a sticker. Parent fires the message send. */
  onSelectSticker?: (sticker: StickerDTO) => void
  /** Called when the user picks a Giphy result. Parent fires the message send. */
  onSelectGif?: (gif: GiphyItemDTO) => void
}

type Tab = 'emoji' | 'stickers' | 'gifs'

/**
 * Telegram-Desktop-style media drawer. Slides in from the right at full
 * viewport height with three tabs (Emoji / Stickers / GIFs) — the same
 * shell the TDesktop client uses.
 *
 * - Emoji tab is `emoji-mart` rendered with `dynamicWidth` so it fills
 *   the drawer; nav lives at the bottom to mirror Telegram's layout.
 * - Stickers and GIFs tabs render placeholder empty-states pending
 *   their own backend work (see docs/SESSION_HANDOFF.md § 7.1).
 *
 * Trigger buttons that toggle the drawer should set
 * `data-emoji-trigger` on themselves so the outside-click detector
 * doesn't immediately re-close after the trigger reopens it.
 */
export default function EmojiPicker({
  onSelect,
  onClose,
  open = true,
  onSelectSticker,
  onSelectGif,
}: Props) {
  const [tab, setTab] = useState<Tab>('emoji')
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleDown = (e: MouseEvent) => {
      const target = e.target as Element | null
      if (!target) return
      if (wrapperRef.current?.contains(target as Node)) return
      // Don't close if the user clicked the trigger that opens us — the
      // trigger's own onClick will toggle, and we'd race it to false → true → false.
      if (typeof target.closest === 'function' && target.closest('[data-emoji-trigger]')) return
      onClose()
    }
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleDown)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handleDown)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [open, onClose])

  return (
    <div
      ref={wrapperRef}
      className={`fixed top-0 right-0 bottom-0 w-full max-w-[380px] z-40 bg-card border-l border-border shadow-2xl flex flex-col transition-transform duration-200 ease-out ${
        open ? 'translate-x-0' : 'translate-x-full pointer-events-none'
      }`}
      data-emoji-drawer
    >
      <div className="flex items-stretch border-b border-border shrink-0">
        <TabButton active={tab === 'emoji'} onClick={() => setTab('emoji')}>
          <Smile size={16} /> Emoji
        </TabButton>
        <TabButton active={tab === 'stickers'} onClick={() => setTab('stickers')}>
          <Sticker size={16} /> Stickers
        </TabButton>
        <TabButton active={tab === 'gifs'} onClick={() => setTab('gifs')}>
          <Film size={16} /> GIFs
        </TabButton>
        <button
          onClick={onClose}
          className="ml-auto px-3 text-muted-foreground hover:text-foreground hover:bg-accent transition"
          title="Close (Esc)"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {tab === 'emoji' && (
          <TgEmojiPicker onSelect={onSelect} />
        )}
        {tab === 'stickers' && onSelectSticker && (
          <StickerPicker onSelect={(s) => { onSelectSticker(s); onClose() }} />
        )}
        {tab === 'stickers' && !onSelectSticker && (
          <div className="p-6 text-center text-muted-foreground text-sm">
            Stickers aren't available in this context.
          </div>
        )}
        {tab === 'gifs' && onSelectGif && (
          <GifPicker onSelect={(g) => { onSelectGif(g); onClose() }} />
        )}
        {tab === 'gifs' && !onSelectGif && (
          <div className="p-6 text-center text-muted-foreground text-sm">
            GIFs aren't available in this context.
          </div>
        )}
      </div>
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition border-b-2 ${
        active
          ? 'text-primary border-primary'
          : 'text-muted-foreground border-transparent hover:text-foreground hover:bg-accent/50'
      }`}
    >
      {children}
    </button>
  )
}
